import React, { useCallback, useRef } from 'react';

export interface ImageAnalysisResult {
  averageBrightness: number;
  dominantColor: string;
  needsStrongOverlay: boolean;
  needsDarkOverlay: boolean;
  contrastRatio: number;
  colorTemperature: 'warm' | 'cool' | 'neutral';
  imageComplexity: 'low' | 'medium' | 'high';
}

interface AdvancedImageAnalyzerProps {
  src: string;
  onAnalysisComplete?: (result: ImageAnalysisResult) => void;
  className?: string;
  silentMode?: boolean;
}

export function AdvancedImageAnalyzer({ 
  src, 
  onAnalysisComplete,
  className = '',
  silentMode = false
}: AdvancedImageAnalyzerProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);

  const analyzeImage = useCallback((img: HTMLImageElement) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    try {
      // Set canvas size to match image (scaled down for performance)
      const maxSize = 100;
      const ratio = Math.min(maxSize / img.width, maxSize / img.height);
      canvas.width = img.width * ratio;
      canvas.height = img.height * ratio;

      // Draw image to canvas
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const data = imageData.data;

      let totalR = 0, totalG = 0, totalB = 0;
      let totalBrightness = 0;
      let pixels = 0;
      
      // Color distribution for dominant color detection
      const colorCounts: { [key: string]: number } = {};
      
      // Analyze each pixel
      for (let i = 0; i < data.length; i += 4) {
        const r = data[i];
        const g = data[i + 1];
        const b = data[i + 2];
        const a = data[i + 3];

        // Skip transparent pixels
        if (a < 128) continue;

        totalR += r;
        totalG += g;
        totalB += b;
        pixels++;

        // Calculate luminance (perceived brightness)
        const brightness = (0.299 * r + 0.587 * g + 0.114 * b);
        totalBrightness += brightness;

        // Quantize colors for dominant color detection
        const quantizedR = Math.floor(r / 32) * 32;
        const quantizedG = Math.floor(g / 32) * 32;
        const quantizedB = Math.floor(b / 32) * 32;
        const colorKey = `${quantizedR},${quantizedG},${quantizedB}`;
        colorCounts[colorKey] = (colorCounts[colorKey] || 0) + 1;
      }

      if (pixels === 0) {
        if (!silentMode) {
          console.warn('🎨 [IMAGE_ANALYZER] No valid pixels found for analysis');
        }
        return;
      }

      // Calculate averages
      const avgR = Math.round(totalR / pixels);
      const avgG = Math.round(totalG / pixels);
      const avgB = Math.round(totalB / pixels);
      const averageBrightness = totalBrightness / pixels;

      // Find dominant color
      let dominantColor = `rgb(${avgR}, ${avgG}, ${avgB})`;
      let maxCount = 0;
      Object.entries(colorCounts).forEach(([color, count]) => {
        if (count > maxCount) {
          maxCount = count;
          dominantColor = `rgb(${color})`;
        }
      });

      // Determine color temperature
      let colorTemperature: 'warm' | 'cool' | 'neutral' = 'neutral';
      if (avgR > avgB + 30) {
        colorTemperature = 'warm';
      } else if (avgB > avgR + 30) {
        colorTemperature = 'cool';
      }

      // Calculate contrast needs
      const needsStrongOverlay = averageBrightness > 180; // Very bright images
      const needsDarkOverlay = averageBrightness > 120;   // Moderately bright images
      
      // Estimate contrast ratio with white text
      const contrastRatio = (255 + 0.05) / (averageBrightness + 0.05);

      // Analyze image complexity (edge detection approximation)
      let complexity = 0;
      for (let i = 0; i < data.length - 8; i += 4) {
        const current = data[i] + data[i + 1] + data[i + 2];
        const next = data[i + 4] + data[i + 5] + data[i + 6];
        complexity += Math.abs(current - next);
      }
      
      const imageComplexity: 'low' | 'medium' | 'high' = 
        complexity < pixels * 30 ? 'low' : 
        complexity < pixels * 80 ? 'medium' : 'high';

      const result: ImageAnalysisResult = {
        averageBrightness: Math.round(averageBrightness),
        dominantColor,
        needsStrongOverlay,
        needsDarkOverlay,
        contrastRatio: Math.round(contrastRatio * 100) / 100,
        colorTemperature,
        imageComplexity
      };

      if (!silentMode) {
        console.log(`🎨 [IMAGE_ANALYZER] ✅ Analysis complete:`, {
          brightness: result.averageBrightness,
          dominant: result.dominantColor,
          temperature: result.colorTemperature,
          complexity: result.imageComplexity,
          strongOverlay: result.needsStrongOverlay,
          darkOverlay: result.needsDarkOverlay,
          contrast: result.contrastRatio
        });
      }

      onAnalysisComplete?.(result);
    } catch (error) {
      if (!silentMode) {
        console.warn('🎨 [IMAGE_ANALYZER] Analysis failed (non-critical):', error?.message);
      }
      
      // Provide fallback analysis result
      const fallbackResult: ImageAnalysisResult = {
        averageBrightness: 128,
        dominantColor: 'rgb(128, 128, 128)',
        needsStrongOverlay: false,
        needsDarkOverlay: true,
        contrastRatio: 2.0,
        colorTemperature: 'neutral',
        imageComplexity: 'medium'
      };
      
      onAnalysisComplete?.(fallbackResult);
    }
  }, [onAnalysisComplete, silentMode]);

  const handleImageLoad = useCallback(() => {
    if (imageRef.current) {
      analyzeImage(imageRef.current);
    }
  }, [analyzeImage]);

  const handleImageError = useCallback(() => {
    if (!silentMode) {
      console.warn('🎨 [IMAGE_ANALYZER] Image failed to load for analysis (non-critical)');
    }
    
    // Provide fallback analysis for failed images
    const fallbackResult: ImageAnalysisResult = {
      averageBrightness: 128,
      dominantColor: 'rgb(128, 128, 128)',
      needsStrongOverlay: false,
      needsDarkOverlay: true,
      contrastRatio: 2.0,
      colorTemperature: 'neutral',
      imageComplexity: 'medium'
    };
    
    onAnalysisComplete?.(fallbackResult);
  }, [onAnalysisComplete, silentMode]);

  return (
    <div className={`hidden ${className}`}>
      <img
        ref={imageRef}
        src={src}
        onLoad={handleImageLoad}
        onError={handleImageError}
        crossOrigin="anonymous"
        style={{ display: 'none' }}
        alt=""
      />
      <canvas
        ref={canvasRef}
        style={{ display: 'none' }}
      />
    </div>
  );
}